import React from 'react';
import { Icon } from '../../ui';

/**
 * Section title for the operator overview: mono eyebrow, optional glyph,
 * optional count and right-side actions. Quiet by design so the standout
 * "What you need to know" section can be the loud one.
 */
export default function SectionHeading({
  eyebrow,
  title,
  icon,
  count,
  actions,
  style,
}: {
  eyebrow?: string;
  title: React.ReactNode;
  icon?: string;
  count?: number | string;
  actions?: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: '12px',
        marginBottom: '12px',
        ...style,
      }}
    >
      <div style={{ minWidth: 0 }}>
        {eyebrow ? (
          <div className="t-eyebrow" style={{ marginBottom: '6px' }}>
            {eyebrow}
          </div>
        ) : null}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {icon ? <Icon name={icon} size={16} style={{ color: 'var(--text-faint)', flexShrink: 0 }} /> : null}
          <span
            style={{
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
              letterSpacing: 'var(--tracking-tight)',
              color: 'var(--text-body)',
              lineHeight: 'var(--leading-tight)',
            }}
          >
            {title}
          </span>
          {count !== undefined ? (
            <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', paddingTop: '2px' }}>
              {count}
            </span>
          ) : null}
        </div>
      </div>
      {actions ? <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>{actions}</div> : null}
    </div>
  );
}

/** Hairline card used throughout the overview. */
export function OpCard({
  children,
  tone = 'card',
  padding = '14px 16px',
  className = '',
  style,
}: {
  children: React.ReactNode;
  tone?: 'card' | 'raised';
  padding?: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      className={className}
      style={{
        background: tone === 'raised' ? 'var(--surface-raised)' : 'var(--surface-card)',
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: tone === 'card' ? 'var(--shadow-rest)' : 'none',
        padding,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** Plain bullet list in the overview's voice. */
export function OpBullets({ items, muted = false }: { items: string[]; muted?: boolean }) {
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {items.map((item, i) => (
        <li
          key={i}
          style={{
            display: 'flex',
            gap: '10px',
            alignItems: 'flex-start',
            fontSize: 'var(--text-sm)',
            lineHeight: 'var(--leading-normal)',
            color: muted ? 'var(--text-muted)' : 'var(--text-body)',
          }}
        >
          <span
            aria-hidden
            style={{
              width: '5px',
              height: '5px',
              borderRadius: 'var(--radius-pill)',
              background: 'var(--text-faint)',
              marginTop: '8px',
              flexShrink: 0,
            }}
          />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

/** Small external-link button rendered as an anchor. */
export function LinkButton({
  href,
  icon = 'external-link',
  children,
  variant = 'ghost',
  size = 'sm',
  disabled = false,
}: {
  href: string;
  icon?: string;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md';
  disabled?: boolean;
}) {
  const variants: Record<string, React.CSSProperties> = {
    primary: { background: 'var(--accent)', color: 'var(--text-on-accent)', border: '1px solid transparent' },
    secondary: { background: 'var(--surface-raised)', color: 'var(--text-body)', border: '1px solid var(--border-hairline)' },
    ghost: { background: 'transparent', color: 'var(--text-muted)', border: '1px solid transparent' },
  };
  const hoverClass = variant === 'primary' ? 'hover-glow' : variant === 'ghost' ? 'hover-wash' : 'hover-lift';
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-disabled={disabled || undefined}
      onClick={disabled ? (e) => e.preventDefault() : undefined}
      className={`${hoverClass} pressable`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '7px',
        height: size === 'sm' ? 'var(--control-sm)' : 'var(--control-md)',
        padding: size === 'sm' ? '0 10px' : '0 14px',
        borderRadius: 'var(--radius-sm)',
        fontFamily: 'var(--font-ui)',
        fontSize: size === 'sm' ? 'var(--text-sm)' : 'var(--text-base)',
        fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
        lineHeight: 1,
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        ...variants[variant],
      }}
    >
      <Icon name={icon} size={size === 'sm' ? 14 : 16} />
      <span>{children}</span>
    </a>
  );
}
